/** Persistência e recuperação de lotes (aba Lotes / Produto por Palete). */

import { resolverNomeProdutoPontuacao, normalizarTextoProdutoImportacao } from './resolverProdutoPontuacao';

export interface LoteProdutoPersistido {
  id: string;
  lote: string;
  produtoNome: string;
  qtd: number;
  dataHoraInicioCorte?: string;
  dataHoraFimEmbalagem?: string;
  /** Cor do PDF (não entra na pontuação). */
  corProduto?: string;
  /** Código interno do produto no PDF. */
  codigoProduto?: string;
}

const CHAVES_LOTES_LEGADO = ['lotes-produtos-ade', 'lotes-produtos'] as const;
const CHAVE_LOTES_FINAL_MANUAL = 'lotes-finalizados-manual-ade';
const CHAVE_LOTES_EXCLUIDOS = 'lotes-produtos-excluidos-ade';
const CHAVE_LOTES_PARES_EXCLUIDOS = 'lotes-produtos-pares-excluidos-ade';
const CHAVE_MIGRACAO_PURGA_8723 = 'lotes-migracao-purga-8723-rack-liv-v1';
const CHAVE_MIGRACAO_PURGA_8833_LUME = 'lotes-migracao-purga-8833-buffet-lume-v2';
const CHAVE_TABELA_CHAVES_LOTES = 'tabela-dias-chaves-lotes-ade';
const CHAVE_TABELA_CHAVES_MANUAL = 'tabela-dias-chaves-manual-ade';
const CHAVE_TABELA_VALORES_MANUAL = 'tabela-dias-valores-manual-ade';
const CHAVE_TABELA_VALORES_MIGRADO = 'tabela-dias-valores-manual-migrado-ade';
const CHAVE_TABELA_DETECT_MANUAL = 'tabela-dias-detect-manual-lotes-ade';
const PRODUTO_STUB_MANUAL = '(completar — só nº marcado em Tempo de Corte)';

/** Chave canônica `ano-mes-dia` (mes/dia sem zero à esquerda, igual à Tabela dos Dias). */
export function normalizarChaveTabelaDia(key: string): string | null {
  const parts = String(key || '').split('-');
  if (parts.length !== 3) return null;
  const ano = Number(parts[0]);
  const mes = Number(parts[1]);
  const dia = Number(parts[2]);
  if (!Number.isFinite(ano) || !Number.isFinite(mes) || !Number.isFinite(dia)) return null;
  if (mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;
  return `${ano}-${mes}-${dia}`;
}

/** Unifica chaves duplicadas (ex.: 2026-06-01 e 2026-6-1) ao carregar a tabela. */
export function normalizarMapaTabelaDias(mapa: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [key, val] of Object.entries(mapa || {})) {
    const canon = normalizarChaveTabelaDia(key);
    if (!canon) continue;
    out[canon] = Number(val) || 0;
  }
  return out;
}

export function lerChavesTabelaGeradasLotes(): Set<string> {
  try {
    const raw = localStorage.getItem(CHAVE_TABELA_CHAVES_LOTES);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed.map((k) => normalizarChaveTabelaDia(String(k))).filter(Boolean) as string[]
    );
  } catch {
    return new Set();
  }
}

export function salvarChavesTabelaGeradasLotes(chaves: Iterable<string>): void {
  try {
    const lista = [...chaves]
      .map((k) => normalizarChaveTabelaDia(k))
      .filter(Boolean) as string[];
    localStorage.setItem(CHAVE_TABELA_CHAVES_LOTES, JSON.stringify(lista));
  } catch {
    /* quota */
  }
}

export function lerChavesTabelaManuais(): Set<string> {
  try {
    const raw = localStorage.getItem(CHAVE_TABELA_CHAVES_MANUAL);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed.map((k) => normalizarChaveTabelaDia(String(k))).filter(Boolean) as string[]
    );
  } catch {
    return new Set();
  }
}

export function registrarChaveTabelaManual(key: string): void {
  const canon = normalizarChaveTabelaDia(key);
  if (!canon) return;
  const set = lerChavesTabelaManuais();
  set.add(canon);
  try {
    localStorage.setItem(CHAVE_TABELA_CHAVES_MANUAL, JSON.stringify([...set]));
  } catch {
    /* quota */
  }
}

/** Valores editados manualmente na Tabela dos Dias (prevalecem sobre sync dos lotes). */
export function lerValoresTabelaManuais(): Record<string, number> {
  try {
    const raw = localStorage.getItem(CHAVE_TABELA_VALORES_MANUAL);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      const canon = normalizarChaveTabelaDia(k);
      if (canon) out[canon] = Number(v) || 0;
    }
    return out;
  } catch {
    return {};
  }
}

export function salvarValorTabelaManual(key: string, valor: number): void {
  const canon = normalizarChaveTabelaDia(key);
  if (!canon) return;
  const map = lerValoresTabelaManuais();
  map[canon] = Number(valor) || 0;
  try {
    localStorage.setItem(CHAVE_TABELA_VALORES_MANUAL, JSON.stringify(map));
  } catch {
    /* quota */
  }
  registrarChaveTabelaManual(canon);
}

/** Mescla mapa salvo com overrides manuais (para gráfico, resumo e carga inicial). */
export function mesclarTabelaComValoresManuais(mapa: Record<string, number>): Record<string, number> {
  const base = normalizarMapaTabelaDias(mapa);
  const manuais = lerValoresTabelaManuais();
  if (Object.keys(manuais).length === 0) return base;
  return normalizarMapaTabelaDias({ ...base, ...manuais });
}

/** Detecta células da tabela que diferem do acumulado dos lotes e trata como manual (uma vez). */
export function detectarESalvarManuaisDistintosDoLotes(
  prevNorm: Record<string, number>,
  acumulado: Record<string, number>
): void {
  if (localStorage.getItem(CHAVE_TABELA_DETECT_MANUAL)) return;
  const map = lerValoresTabelaManuais();
  let alterou = false;
  for (const [k, v] of Object.entries(prevNorm)) {
    const val = Number(v) || 0;
    if (!val) continue;
    const auto = acumulado[k];
    if (auto == null || Math.abs(val - (Number(auto) || 0)) > 0.001) {
      if (map[k] !== val) {
        map[k] = val;
        alterou = true;
      }
    }
  }
  if (alterou) {
    try {
      localStorage.setItem(CHAVE_TABELA_VALORES_MANUAL, JSON.stringify(map));
    } catch {
      /* quota */
    }
  }
  try {
    localStorage.setItem(CHAVE_TABELA_DETECT_MANUAL, '1');
  } catch {
    /* quota */
  }
}

/** Migra chaves manuais antigas + valores já salvos na tabela para o mapa persistente. */
export function migrarValoresManuaisTabela(prevNorm: Record<string, number>): void {
  if (localStorage.getItem(CHAVE_TABELA_VALORES_MIGRADO)) return;
  const map = lerValoresTabelaManuais();
  for (const k of lerChavesTabelaManuais()) {
    if (k in prevNorm && !(k in map)) map[k] = Number(prevNorm[k]) || 0;
  }
  try {
    localStorage.setItem(CHAVE_TABELA_VALORES_MANUAL, JSON.stringify(map));
    localStorage.setItem(CHAVE_TABELA_VALORES_MIGRADO, '1');
  } catch {
    /* quota */
  }
}

/** Soma pontuação de um mês na Tabela dos Dias (mesma regra da linha “Soma”). */
export function somarPontuacaoMesTabelaDias(
  mapa: Record<string, number>,
  ano: number,
  mes: number
): number {
  const norm = normalizarMapaTabelaDias(mapa);
  const diasNoMes = new Date(ano, mes, 0).getDate();
  let total = 0;
  for (let dia = 1; dia <= diasNoMes; dia++) {
    total += Number(norm[`${ano}-${mes}-${dia}`]) || 0;
  }
  return total;
}

/** Agrega pontuação por `YYYY-MM` a partir do mapa da Tabela dos Dias. */
export function agregarPontuacaoPorMesTabelaDias(
  mapa: Record<string, number>
): Record<string, number> {
  const norm = normalizarMapaTabelaDias(mapa);
  const soma: Record<string, number> = {};
  for (const [key, val] of Object.entries(norm)) {
    const parts = key.split('-');
    if (parts.length !== 3) continue;
    const ano = Number(parts[0]);
    const mes = Number(parts[1]);
    if (!Number.isFinite(ano) || !Number.isFinite(mes)) continue;
    const k = `${ano}-${String(mes).padStart(2, '0')}`;
    soma[k] = (soma[k] ?? 0) + (Number(val) || 0);
  }
  return soma;
}

function chaveTabelaDiaDeData(data: Date): string {
  return `${data.getFullYear()}-${data.getMonth() + 1}-${data.getDate()}`;
}

/** Mês civil do lote (`YYYY-MM`) — mesma regra do filtro por mês na aba Lotes. */
export function chaveMesRegistroLote(lp: LoteProdutoPersistido): string | null {
  const raw = lp.dataHoraInicioCorte || lp.dataHoraFimEmbalagem;
  if (!raw) return null;
  const data = new Date(raw);
  if (isNaN(data.getTime())) return null;
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  return `${data.getFullYear()}-${mes}`;
}

function normalizarChaveLote(s: string): string {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();
}

export function extrairNumeroLote(lote: string): number {
  const m = String(lote || '').match(/\d+/);
  return m ? parseInt(m[0], 10) : 0;
}

function chaveUnicaLote(lp: LoteProdutoPersistido): string {
  const id = String(lp?.id || '').trim();
  if (id) return `id:${id}`;
  const cor = normalizarChaveLote(lp.corProduto || '');
  const cod = normalizarChaveLote(lp.codigoProduto || '');
  return `lp:${normalizarChaveLote(lp.lote)}|${normalizarChaveLote(lp.produtoNome)}|${cor}|${cod}|${Number(lp.qtd) || 0}|${lp.dataHoraInicioCorte || ''}|${lp.dataHoraFimEmbalagem || ''}`;
}

/** IDs removidos explicitamente na aba Lotes — impedem “ressuscitar” no sync/backup. */
export function lerIdsLotesExcluidos(): Set<string> {
  try {
    const raw = localStorage.getItem(CHAVE_LOTES_EXCLUIDOS);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.map((x) => String(x || '').trim()).filter(Boolean));
  } catch {
    return new Set();
  }
}

export function registrarIdsLotesExcluidos(ids: string[]): void {
  const limpos = ids.map((x) => String(x || '').trim()).filter(Boolean);
  if (limpos.length === 0) return;
  const set = lerIdsLotesExcluidos();
  limpos.forEach((id) => set.add(id));
  const lista = [...set].slice(-4000);
  try {
    localStorage.setItem(CHAVE_LOTES_EXCLUIDOS, JSON.stringify(lista));
  } catch {
    /* quota */
  }
}

/** Pares lote+produto removidos — bloqueia ressuscitar com id diferente (sync / Tempo de Corte). */
export function lerParesLotesExcluidos(): Set<string> {
  try {
    const raw = localStorage.getItem(CHAVE_LOTES_PARES_EXCLUIDOS);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.map((x) => String(x || '').trim()).filter(Boolean));
  } catch {
    return new Set();
  }
}

export function registrarParesLotesExcluidos(pares: string[]): void {
  const limpos = pares.map((x) => String(x || '').trim()).filter(Boolean);
  if (limpos.length === 0) return;
  const set = lerParesLotesExcluidos();
  limpos.forEach((par) => set.add(par));
  const lista = [...set].slice(-4000);
  try {
    localStorage.setItem(CHAVE_LOTES_PARES_EXCLUIDOS, JSON.stringify(lista));
  } catch {
    /* quota */
  }
}

/** Reimportação Excel / cadastro manual — libera pares bloqueados para voltarem a aparecer. */
export function liberarParesLotesExcluidos(pares: string[]): void {
  const limpos = pares.map((x) => String(x || '').trim()).filter(Boolean);
  if (limpos.length === 0) return;
  const set = lerParesLotesExcluidos();
  let mudou = false;
  const basesLiberar = new Set(limpos.map((p) => baseParExcluido(p)));
  for (const existente of [...set]) {
    const base = baseParExcluido(existente);
    if (set.has(existente) && (limpos.includes(existente) || basesLiberar.has(base) || basesLiberar.has(existente))) {
      if (set.delete(existente)) mudou = true;
    }
  }
  if (!mudou) return;
  try {
    localStorage.setItem(CHAVE_LOTES_PARES_EXCLUIDOS, JSON.stringify([...set].slice(-4000)));
  } catch {
    /* quota */
  }
}

/** Lote + nome do produto (sem cor/código) — chave de exclusão contra ressuscitar. */
export function chaveParLoteProdutoNome(
  lp: Pick<LoteProdutoPersistido, 'lote' | 'produtoNome'>
): string {
  return `${normalizarChaveLote(lp.lote)}|${normalizarChaveLote(lp.produtoNome)}`;
}

function baseParExcluido(par: string): string {
  const partes = String(par || '').split('|');
  if (partes.length < 2) return String(par || '').trim();
  return `${partes[0]}|${partes[1]}`;
}

/** Pares a gravar na exclusão: lote|nome e lote|nome|cor|código. */
export function paresExclusaoDeLoteProduto(
  lp: Pick<LoteProdutoPersistido, 'lote' | 'produtoNome' | 'corProduto' | 'codigoProduto'>
): string[] {
  const base = chaveParLoteProdutoNome(lp);
  const full = chaveParLoteProduto(lp);
  return base === full ? [base] : [base, full];
}

function basesParesExcluidos(excPares: Set<string>): Set<string> {
  const bases = new Set<string>();
  for (const p of excPares) {
    const t = String(p || '').trim();
    if (!t) continue;
    bases.add(t);
    bases.add(baseParExcluido(t));
  }
  return bases;
}

function nomesProdutoExclusaoIguais(nomeItem: string, nomeExcluido: string): boolean {
  const a = normalizarChaveLote(nomeItem).replace(/\s+/g, ' ');
  const b = normalizarChaveLote(nomeExcluido).replace(/\s+/g, ' ');
  if (!a || !b) return false;
  if (a === b) return true;
  return a.startsWith(`${b} `) || b.startsWith(`${a} `);
}

function loteCoincideExclusao(loteItem: string, loteExcluido: string): boolean {
  const nItem = extrairNumeroLote(loteItem);
  const nExc = extrairNumeroLote(loteExcluido);
  if (nItem > 0 && nExc > 0) return nItem === nExc;
  return normalizarChaveLote(loteItem) === normalizarChaveLote(loteExcluido);
}

/** Usuário confirmou: Buffet Lume não pertence ao lote 8833 — qualquer variante do nome. */
function ehBuffetLumeDoLote8833(
  lp: Pick<LoteProdutoPersistido, 'lote' | 'produtoNome'>
): boolean {
  if (extrairNumeroLote(lp.lote) !== 8833) return false;
  const nome = normalizarChaveLote(lp.produtoNome);
  return /\bBUFFET\s+LUME\b/.test(nome) || nome.includes('BUFFET LUME');
}

function loteProdutoEstaExcluido(
  lp: Pick<LoteProdutoPersistido, 'id' | 'lote' | 'produtoNome' | 'corProduto' | 'codigoProduto'>,
  excIds: Set<string>,
  basesExc: Set<string>
): boolean {
  if (ehBuffetLumeDoLote8833(lp)) return true;
  const id = String(lp.id || '').trim();
  if (id && excIds.has(id)) return true;
  const base = chaveParLoteProdutoNome(lp);
  if (base === '|') return false;
  if (basesExc.has(base) || basesExc.has(chaveParLoteProduto(lp))) return true;
  const loteItem = String(lp.lote || '');
  const nomeItem = String(lp.produtoNome || '');
  for (const par of basesExc) {
    const partes = String(par).split('|');
    if (partes.length < 2) continue;
    if (loteCoincideExclusao(loteItem, partes[0]) && nomesProdutoExclusaoIguais(nomeItem, partes[1])) {
      return true;
    }
  }
  return false;
}

export function filtrarLotesNaoExcluidos<
  T extends {
    id: string;
    lote?: string;
    produtoNome?: string;
    corProduto?: string;
    codigoProduto?: string;
  },
>(lista: T[]): T[] {
  const excIds = lerIdsLotesExcluidos();
  const excPares = lerParesLotesExcluidos();
  const basesExc = basesParesExcluidos(excPares);
  return lista.filter((lp) => {
    if (lp.lote === undefined && lp.produtoNome === undefined) {
      const id = String(lp.id || '').trim();
      return !(id && excIds.has(id));
    }
    return !loteProdutoEstaExcluido(lp as LoteProdutoPersistido, excIds, basesExc);
  });
}

function pontuacaoCompletude(lp: LoteProdutoPersistido): number {
  return (
    (lp.dataHoraInicioCorte ? 2 : 0) +
    (lp.dataHoraFimEmbalagem ? 2 : 0) +
    (String(lp.id || '').trim() ? 1 : 0)
  );
}

/** Une listas de lotes sem descartar entradas de nenhuma fonte. */
export function mesclarLotesProdutos(
  ...listas: LoteProdutoPersistido[][]
): LoteProdutoPersistido[] {
  const map = new Map<string, LoteProdutoPersistido>();
  for (const lista of listas) {
    if (!Array.isArray(lista)) continue;
    for (const bruto of lista) {
      if (!bruto || typeof bruto !== 'object') continue;
      const lp: LoteProdutoPersistido = {
        id: String(bruto.id || '').trim() || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        lote: String(bruto.lote || '').trim(),
        produtoNome: String(bruto.produtoNome || '').trim(),
        qtd: Number(bruto.qtd) || 0,
        dataHoraInicioCorte: bruto.dataHoraInicioCorte,
        dataHoraFimEmbalagem: bruto.dataHoraFimEmbalagem,
        corProduto: bruto.corProduto ? String(bruto.corProduto).trim() : undefined,
        codigoProduto: bruto.codigoProduto ? String(bruto.codigoProduto).trim() : undefined,
      };
      if (!lp.lote && !lp.produtoNome) continue;
      const k = chaveUnicaLote(lp);
      const prev = map.get(k);
      if (!prev || pontuacaoCompletude(lp) >= pontuacaoCompletude(prev)) {
        map.set(k, lp);
      }
    }
  }
  const excIds = lerIdsLotesExcluidos();
  const basesExc = basesParesExcluidos(lerParesLotesExcluidos());
  return [...map.values()]
    .filter((lp) => !loteProdutoEstaExcluido(lp, excIds, basesExc))
    .sort((a, b) => {
    const na = extrairNumeroLote(a.lote);
    const nb = extrairNumeroLote(b.lote);
    if (na !== nb) return na - nb;
    return (a.lote || '').localeCompare(b.lote || '', 'pt-BR');
  });
}

function isIdRecuperacaoAutomatica(id: string): boolean {
  return /^(rec-tc|rec-orig|rec-manual)-/.test(String(id || '').trim());
}

function isProdutoStubLote(nome: string): boolean {
  return normalizarChaveLote(nome) === normalizarChaveLote(PRODUTO_STUB_MANUAL);
}

/** Chave de unicidade: mesmo lote + produto + cor + código = uma linha; cores diferentes = linhas separadas. */
export function chaveParLoteProduto(
  lp: Pick<LoteProdutoPersistido, 'lote' | 'produtoNome' | 'corProduto' | 'codigoProduto'>
): string {
  const cor = normalizarChaveLote(lp.corProduto || '');
  const cod = normalizarChaveLote(lp.codigoProduto || '');
  return `${normalizarChaveLote(lp.lote)}|${normalizarChaveLote(lp.produtoNome)}|${cor}|${cod}`;
}

/**
 * Purga única: lote 8723 + RACK LIV 2.18 não existe no cadastro real — usuário confirmou fantasma.
 */
export function aplicarMigracaoPurgaFantasma8723(): void {
  try {
    if (localStorage.getItem(CHAVE_MIGRACAO_PURGA_8723)) return;
    registrarParesLotesExcluidos([
      chaveParLoteProduto({ lote: '8723', produtoNome: 'RACK LIV 2.18' }),
    ]);
    localStorage.setItem(CHAVE_MIGRACAO_PURGA_8723, '1');
  } catch {
    /* ignora */
  }
}

/**
 * Purga: lote 8833 + BUFFET LUME voltava após exclusão porque a chave antiga
 * incluía cor/código e a cópia sem cor (Tempo de Corte / outra janela) passava.
 */
export function aplicarMigracaoPurga8833BuffetLume(): void {
  try {
    if (localStorage.getItem(CHAVE_MIGRACAO_PURGA_8833_LUME)) return;
    registrarParesLotesExcluidos(
      paresExclusaoDeLoteProduto({ lote: '8833', produtoNome: 'BUFFET LUME' })
    );
    localStorage.setItem(CHAVE_MIGRACAO_PURGA_8833_LUME, '1');
  } catch {
    /* ignora */
  }
}

/** Escolhe a linha “válida” quando há duplicata do mesmo lote + produto. */
export function prioridadeLinhaLote(
  a: LoteProdutoPersistido,
  b: LoteProdutoPersistido
): LoteProdutoPersistido {
  const recA = isIdRecuperacaoAutomatica(a.id);
  const recB = isIdRecuperacaoAutomatica(b.id);
  if (recA !== recB) return recA ? b : a;

  const stubA = isProdutoStubLote(a.produtoNome);
  const stubB = isProdutoStubLote(b.produtoNome);
  if (stubA !== stubB) return stubA ? b : a;

  const qtdA = Number(a.qtd) || 0;
  const qtdB = Number(b.qtd) || 0;
  if (qtdA !== qtdB) return qtdA > qtdB ? a : b;

  return pontuacaoCompletude(a) >= pontuacaoCompletude(b) ? a : b;
}

/**
 * Uma linha por lote + produto + cor + código (cadastro prevalece sobre rec-tc / stub).
 * Mesmo produto no mesmo lote com cores diferentes permanece em linhas separadas.
 */
export function deduplicarLotesPorLoteProduto(
  lista: LoteProdutoPersistido[],
  opcoes?: { registrarExcluidos?: boolean; preservarOrdem?: boolean }
): LoteProdutoPersistido[] {
  const map = new Map<string, LoteProdutoPersistido>();
  for (const lp of lista) {
    if (!lp?.lote && !lp?.produtoNome) continue;
    const k = chaveParLoteProduto(lp);
    const prev = map.get(k);
    map.set(k, prev ? prioridadeLinhaLote(prev, lp) : lp);
  }

  const ordenarPorLote = (a: LoteProdutoPersistido, b: LoteProdutoPersistido) => {
    const na = extrairNumeroLote(a.lote);
    const nb = extrairNumeroLote(b.lote);
    if (na !== nb) return na - nb;
    return (a.lote || '').localeCompare(b.lote || '', 'pt-BR');
  };

  let winners: LoteProdutoPersistido[];
  if (opcoes?.preservarOrdem) {
    const seen = new Set<string>();
    winners = [];
    for (const lp of lista) {
      const k = chaveParLoteProduto(lp);
      const w = map.get(k);
      if (!w || seen.has(k)) continue;
      seen.add(k);
      winners.push(w);
    }
    for (const w of map.values()) {
      const k = chaveParLoteProduto(w);
      if (seen.has(k)) continue;
      seen.add(k);
      winners.push(w);
    }
  } else {
    winners = [...map.values()].sort(ordenarPorLote);
  }

  if (opcoes?.registrarExcluidos) {
    const idsVencedores = new Set(winners.map((w) => String(w.id || '').trim()).filter(Boolean));
    const idsPerdidos = lista
      .map((lp) => String(lp.id || '').trim())
      .filter((id) => id && !idsVencedores.has(id));
    if (idsPerdidos.length > 0) registrarIdsLotesExcluidos(idsPerdidos);
  }

  return filtrarLotesNaoExcluidos(winners);
}

/**
 * Após editar um lote na aba Lotes: remove duplicatas da mesma chave e linhas órfãs
 * da chave antiga (ex.: corrigiu lote 8723 → 9000 mas o 8723 fantasma permanecia).
 */
export function mesclarEdicaoLoteSalva(
  lista: LoteProdutoPersistido[],
  antes: LoteProdutoPersistido,
  atualizado: LoteProdutoPersistido
): LoteProdutoPersistido[] {
  const idEditado = String(atualizado.id || '').trim();
  const chaveAntiga = chaveParLoteProduto(antes);
  const chaveNova = chaveParLoteProduto(atualizado);
  const idsRemover: string[] = [];

  const filtrado = lista
    .map((lp) => (String(lp.id || '').trim() === idEditado ? atualizado : lp))
    .filter((lp) => {
      const id = String(lp.id || '').trim();
      if (id === idEditado) return true;
      const ch = chaveParLoteProduto(lp);
      if (ch === chaveNova) {
        idsRemover.push(id);
        return false;
      }
      if (chaveNova !== chaveAntiga && ch === chaveAntiga) {
        idsRemover.push(id);
        return false;
      }
      return true;
    });

  if (idsRemover.length > 0) registrarIdsLotesExcluidos(idsRemover);
  if (chaveNova !== chaveAntiga) registrarParesLotesExcluidos([chaveAntiga]);
  return filtrado;
}

/**
 * Sync entre janelas: disco (IndexedDB) prevalece por id; remove fantasmas com chave
 * antiga que já não existe no cadastro salvo.
 */
export function aplicarSyncLotesProdutosRemoto(
  prev: LoteProdutoPersistido[],
  mesclados: LoteProdutoPersistido[]
): LoteProdutoPersistido[] {
  const disk = deduplicarLotesPorLoteProduto(filtrarLotesNaoExcluidos(mesclados));
  if (disk.length === 0) return deduplicarLotesPorLoteProduto(filtrarLotesNaoExcluidos(prev));

  const excIds = lerIdsLotesExcluidos();
  const basesExc = basesParesExcluidos(lerParesLotesExcluidos());
  const mapId = new Map(disk.map((lp) => [String(lp.id || '').trim(), lp]));
  const idsDisk = new Set(mapId.keys());
  const chavesDisk = new Set(disk.map((lp) => chaveParLoteProduto(lp)));

  const out: LoteProdutoPersistido[] = [...disk];
  const idsOut = new Set(out.map((lp) => String(lp.id || '').trim()).filter(Boolean));
  const chavesOut = new Set(out.map((lp) => chaveParLoteProduto(lp)));

  for (const lp of prev) {
    const id = String(lp.id || '').trim();
    if (id && idsDisk.has(id)) continue;
    const ch = chaveParLoteProduto(lp);
    if (loteProdutoEstaExcluido(lp, excIds, basesExc)) continue;
    if (chavesDisk.has(ch) || chavesOut.has(ch)) continue;
    if (id && idsOut.has(id)) continue;
    out.push(lp);
    if (id) idsOut.add(id);
    chavesOut.add(ch);
  }

  return deduplicarLotesPorLoteProduto(filtrarLotesNaoExcluidos(out));
}

/** Mesmo lote + produto + cor: uma linha só (evita dobrar qtd no Tempo de Corte). */
export function colapsarDuplicatasLoteProduto(
  lista: LoteProdutoPersistido[]
): LoteProdutoPersistido[] {
  return deduplicarLotesPorLoteProduto(lista);
}

/** m² do produto — casamento com a Pontuação (exato + aliases do Excel). */
export function buscarM2ProdutoBoletim(
  nomeProd: string,
  pontuacoes: ReadonlyArray<{ nome: string; m2: number }>
): number {
  const alvo = normalizarTextoProdutoImportacao(nomeProd);
  if (!alvo) return 0;

  const exact = pontuacoes.find((p) => normalizarTextoProdutoImportacao(p.nome) === alvo);
  if (exact) return Number(exact.m2) || 0;

  const nomes = pontuacoes.map((p) => p.nome);
  const resolvido = resolverNomeProdutoPontuacao(nomeProd, nomes);
  if (resolvido) {
    const match = pontuacoes.find(
      (p) => normalizarTextoProdutoImportacao(p.nome) === normalizarTextoProdutoImportacao(resolvido)
    );
    if (match) return Number(match.m2) || 0;
  }

  const lista = pontuacoes.map((p) => ({ ...p, nomeNorm: normalizarTextoProdutoImportacao(p.nome) }));
  let parcial = lista.find((p) => p.nomeNorm === alvo);
  if (!parcial) parcial = lista.find((p) => p.nomeNorm.includes(alvo) || alvo.includes(p.nomeNorm));
  return parcial ? Number(parcial.m2) || 0 : 0;
}

/** Pontuação de uma linha de lote (m² × qtd). */
export function pontosLinhaLoteBoletim(
  lp: LoteProdutoPersistido,
  pontuacoes: ReadonlyArray<{ nome: string; m2: number }>
): number {
  return buscarM2ProdutoBoletim(lp.produtoNome, pontuacoes) * (Number(lp.qtd) || 0);
}

/** Chave do dia civil a partir de Início Corte (fallback Fim Embalagem se início ausente). */
export function dayKeyInicioCorteLote(lp: LoteProdutoPersistido): string | null {
  const raw = lp.dataHoraInicioCorte || lp.dataHoraFimEmbalagem;
  if (!raw) return null;
  const data = new Date(raw);
  if (isNaN(data.getTime())) return null;
  return chaveTabelaDiaDeData(data);
}

/**
 * Soma pontuação (m² × qtd) por dia civil — **uma linha de lote = um lançamento**,
 * igual à soma manual da coluna Pontuação na aba Lotes (sem deduplicar linhas).
 * Início Corte define o dia; se ausente, usa Fim Embalagem.
 */
export function calcularPontuacaoLotesPorDia(
  lotes: LoteProdutoPersistido[],
  pontuacoes: ReadonlyArray<{ nome: string; m2: number }>
): Record<string, number> {
  const acumulado: Record<string, number> = {};

  for (const lp of lotes) {
    const dayKey = dayKeyInicioCorteLote(lp);
    if (!dayKey) continue;
    const pontos = pontosLinhaLoteBoletim(lp, pontuacoes);
    acumulado[dayKey] = (acumulado[dayKey] ?? 0) + pontos;
  }

  return acumulado;
}

const CHAVE_TABELA_MIGRACAO_V2 = 'tabela-dias-migracao-v2-ade';
const CHAVE_TABELA_MIGRACAO_V3 = 'tabela-dias-migracao-v3-ade';

/**
 * Aplica sync lotes → Tabela dos Dias: remove chaves geradas antes, grava acumulado,
 * preserva células editadas manualmente, migração v3 limpa órfãos da v2.
 */
export function aplicarSyncTabelaDiasComLotes(
  prev: Record<string, number>,
  acumulado: Record<string, number>,
  chavesGeradasAnterior: Set<string>
): { next: Record<string, number>; novasChaves: Set<string> } {
  const prevNorm = normalizarMapaTabelaDias(prev);
  migrarValoresManuaisTabela(prevNorm);
  detectarESalvarManuaisDistintosDoLotes(prevNorm, acumulado);
  const valoresManuais = lerValoresTabelaManuais();
  const chavesManuais = new Set(Object.keys(valoresManuais));
  const chavesPersistidas = lerChavesTabelaGeradasLotes();
  const chavesRemover = new Set<string>([...chavesPersistidas, ...chavesGeradasAnterior]);

  let next: Record<string, number>;
  const precisaMigracaoV3 = !localStorage.getItem(CHAVE_TABELA_MIGRACAO_V3);

  if (precisaMigracaoV3) {
    next = { ...acumulado, ...valoresManuais };
    try {
      localStorage.setItem(CHAVE_TABELA_MIGRACAO_V3, '1');
      localStorage.setItem(CHAVE_TABELA_MIGRACAO_V2, '1');
    } catch {
      /* quota */
    }
  } else {
    next = { ...prevNorm };
    chavesRemover.forEach((k) => {
      if (chavesManuais.has(k)) return;
      delete next[k];
    });
    for (const [k, v] of Object.entries(acumulado)) {
      if (chavesManuais.has(k)) continue;
      next[k] = v;
    }
    for (const [k, v] of Object.entries(valoresManuais)) {
      next[k] = v;
    }
  }

  const novasChaves = new Set(Object.keys(acumulado));
  return { next: normalizarMapaTabelaDias(next), novasChaves };
}

/** Soma pontuação dos lotes cujo mês civil bate com `YYYY-MM` (filtro da aba Lotes). */
export function somarPontuacaoLotesPorMes(
  lotes: LoteProdutoPersistido[],
  mesAno: string,
  pontuacoes: ReadonlyArray<{ nome: string; m2: number }>
): number {
  if (!mesAno) return 0;
  let total = 0;
  for (const lp of lotes) {
    if (chaveMesRegistroLote(lp) !== mesAno) continue;
    total += pontosLinhaLoteBoletim(lp, pontuacoes);
  }
  return total;
}

export function lerLotesLegadoLocalStorage(): LoteProdutoPersistido[] {
  const fontes: LoteProdutoPersistido[][] = [];
  for (const key of CHAVES_LOTES_LEGADO) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) fontes.push(parsed as LoteProdutoPersistido[]);
    } catch {
      /* ignora */
    }
  }
  return mesclarLotesProdutos(...fontes);
}

/** Sincroniza todas as chaves legadas do localStorage (evita cópia antiga sobrescrever no reload). */
export function salvarLotesLegadoLocalStorage(items: LoteProdutoPersistido[]): void {
  const json = JSON.stringify(items);
  for (const key of CHAVES_LOTES_LEGADO) {
    try {
      localStorage.setItem(key, json);
    } catch {
      /* quota */
    }
  }
}

/** Números marcados em Tempo de Corte → "Finalizar lote antigo" (lista separada do cadastro). */
export function lerLotesFinalizadosManual(): string[] {
  try {
    const raw = localStorage.getItem(CHAVE_LOTES_FINAL_MANUAL);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.map((x) => String(x || '').trim()).filter(Boolean)
      : [];
  } catch {
    return [];
  }
}

/** Reconstrói linhas de lote a partir dos produtos salvos no Tempo de Corte. */
export function reconstruirLotesDeProdutosTempoCorte(
  produtos: Record<string, unknown>[]
): LoteProdutoPersistido[] {
  const out: LoteProdutoPersistido[] = [];
  const vistos = new Set<string>();

  const push = (bruto: Record<string, unknown>, prefixoId: string) => {
    const nome = String(bruto?.nome || '').trim();
    const lote = String(bruto?.lote || '').trim();
    if (!nome || !lote) return;
    const k = `${normalizarChaveLote(lote)}|${normalizarChaveLote(nome)}`;
    if (vistos.has(k)) return;
    vistos.add(k);
    out.push({
      id: `${prefixoId}-${lote}-${nome}`.replace(/\s+/g, '_').slice(0, 120),
      lote,
      produtoNome: nome,
      qtd: Number(bruto?.qtdProduto) || 0,
      dataHoraInicioCorte:
        typeof bruto?.dataHoraInicioProducao === 'string'
          ? bruto.dataHoraInicioProducao
          : undefined,
      dataHoraFimEmbalagem:
        typeof bruto?.dataHoraTerminoProducao === 'string'
          ? bruto.dataHoraTerminoProducao
          : undefined,
    });
  };

  for (const p of produtos) {
    if (!p || typeof p !== 'object') continue;
    push(p, 'rec-tc');
    const origens = p.produtosOrigemCompleto;
    if (Array.isArray(origens)) {
      for (const o of origens) {
        if (o && typeof o === 'object') push(o as Record<string, unknown>, 'rec-orig');
      }
    }
  }
  return filtrarLotesNaoExcluidos(out);
}

/** Só cria stub para nº de lote que ainda não existe no cadastro. */
export function lotesStubDeMarcadosManual(
  atuais: LoteProdutoPersistido[],
  numeros: string[]
): LoteProdutoPersistido[] {
  const numsPresentes = new Set(
    atuais.map((lp) => extrairNumeroLote(lp.lote)).filter((n) => n > 0)
  );
  return numeros
    .filter((lote) => {
      const n = extrairNumeroLote(lote);
      return n > 0 && !numsPresentes.has(n);
    })
    .map((lote) => ({
      id: `rec-manual-${lote}`,
      lote,
      produtoNome: PRODUTO_STUB_MANUAL,
      qtd: 0,
    }));
}

/** Fontes que sobreviveram ao bug das janelas (Tempo de Corte + marcas manuais). */
export function coletarLotesFontesAlternativas(
  produtosTempoCorte: Record<string, unknown>[] = []
): LoteProdutoPersistido[] {
  const deTc = reconstruirLotesDeProdutosTempoCorte(produtosTempoCorte);
  const deManual = lotesStubDeMarcadosManual(deTc, lerLotesFinalizadosManual());
  return mesclarLotesProdutos(deTc, deManual);
}

export function resumoLotes(lista: LoteProdutoPersistido[]): {
  total: number;
  maiorNumeroLote: number;
  lotesUnicos: number;
} {
  const nums = new Set<number>();
  let maior = 0;
  for (const lp of lista) {
    const n = extrairNumeroLote(lp.lote);
    if (n > 0) nums.add(n);
    if (n > maior) maior = n;
  }
  return { total: lista.length, maiorNumeroLote: maior, lotesUnicos: nums.size };
}

/**
 * Repõe registros ausentes (por id ou par lote+produto) — só para recuperação manual.
 * Não usar em gravação automática após exclusão do usuário.
 */
export function recuperarLotesFaltantesNoFinal(
  items: LoteProdutoPersistido[],
  existente: LoteProdutoPersistido[]
): LoteProdutoPersistido[] {
  if (!existente.length) return items;
  const maxItems = Math.max(0, ...items.map((lp) => extrairNumeroLote(lp.lote)));
  const ids = new Set(items.map((lp) => String(lp.id || '').trim()).filter(Boolean));
  const chaves = new Set(
    items.map((lp) => `${normalizarChaveLote(lp.lote)}|${normalizarChaveLote(lp.produtoNome)}`)
  );
  const excIds = lerIdsLotesExcluidos();
  const basesExc = basesParesExcluidos(lerParesLotesExcluidos());
  const extras = existente.filter((lp) => {
    const id = String(lp.id || '').trim();
    if (loteProdutoEstaExcluido(lp, excIds, basesExc)) return false;
    if (id && ids.has(id)) return false;
    if (chaves.has(`${normalizarChaveLote(lp.lote)}|${normalizarChaveLote(lp.produtoNome)}`)) return false;
    const n = extrairNumeroLote(lp.lote);
    return n > maxItems || maxItems === 0;
  });
  if (!extras.length) return items;
  return mesclarLotesProdutos(items, extras);
}
